import React, { useEffect, useState, useRef } from "react";
import { Button, TextField, Autocomplete } from "@mui/material";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { fetchCategoryEvents } from "../../Apiservices/Auth/servers/fetchCategoryEvents";
import * as Yup from "yup";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Calendar } from "primereact/calendar";
import { locale, addLocale } from "primereact/api";
import config from "../../config";
import { createEvent } from "../../Apiservices/Auth/servers/createEvents";
import { useSelector } from "react-redux";

import { ReactComponent as EVENT_CALENDAR } from "../assets/event-calendar.svg";

const ThurentEventForm = ({
  onCloseDailog,
  refreshEvents,
  preSelectedEvent = null,
}) => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [error, setError] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [eventData, setEventData] = useState([]);

  const eventLocalStorageKey = config.eventLocalStorageKey;
  const today = new Date();
  const oneYearFromToday = new Date(
    new Date().setFullYear(today.getFullYear() + 1)
  );

  addLocale("in", {
    now: "OK",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = "";
        const [events_data] = await Promise.all([fetchCategoryEvents(token)]);
        setEventData(events_data);
      } catch (error) {
        setError("Failed to fetch data.");
      }
    };
    fetchData();
    let existingData;
    console.log("existingData", existingData);
    if (existingData) {
      const parsedData = JSON.parse(existingData);

      // Ensure date is set as a Date object
      if (parsedData.Date) {
        parsedData.Date = new Date(parsedData.Date);
      }

      formik.setValues(parsedData); // Set form values with existing data
    }
    locale("in");

    if (preSelectedEvent) {
      handleEventInputChange(null, preSelectedEvent?.name);
    }
  }, []);

  const handleEventInputChange = (event, value) => {
    // Update Formik field value
    handleEventChange("event", value);
    if (value.length >= 3) {
      const newFilteredOptions = eventData
        .map((e) => e.name) // Ensure you're mapping to the right property
        .filter((option) => option.toLowerCase().includes(value.toLowerCase()));

      // Filter options based on input
      setFilteredOptions(newFilteredOptions); // Update the filtered options
    } else {
      setFilteredOptions([]);
    }
  };

  const alertdialog = () => {
    confirmDialog({
      message: "Please Login to Create Event",
      header: "Login",
      defaultFocus: "accept",
      accept: () => {
        const existingData = localStorage.getItem(eventLocalStorageKey);
        if (existingData) {
          const parsedData = JSON.parse(existingData);
          const updatedValues = { ...parsedData, isPending: true };
          localStorage.setItem(
            eventLocalStorageKey,
            JSON.stringify(updatedValues)
          );
        }
        navigate("/login");
      },
    });
  };

  const formik = useFormik({
    initialValues: {
      Date: "",
      eventTitle: "",
      eventDesc: "",
      event: { event: "", id: "" },
    },
    validationSchema: Yup.object({
      event: Yup.object({
        event: Yup.string().required("Event is required"),
      }),
      Date: Yup.string().required("Date is required"),
    }),
    onSubmit: async (values) => {
      try {
        if (isLoggedIn) {
          const eventBody = {
            eventTitle: values.eventTitle,
            event: values.event,
            eventDesc: values.eventDesc,
            Date: values.Date && values.Date.toString(),
          };
          try {
            await createEvent(eventBody);
            if (refreshEvents) {
              refreshEvents();
            }
          } catch (error) {
            console.log("Something Went Wrong while Creating Event ");
            console.log(error);
          } finally {
            formik.resetForm(); 
            localStorage.removeItem(eventLocalStorageKey); 
            if (onCloseDailog) onCloseDailog();
          }
        } else {
          alertdialog();
        }
        console.log(values);
      } catch (ex) {
        setError(ex.toString());
      }
    },
  });

  // Common method to update local storage
  const updateLocalStorage = (updatedValues) => {
    if (!isLoggedIn) {
      localStorage.setItem(eventLocalStorageKey, JSON.stringify(updatedValues));
    }
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    formik.setFieldValue(field, value);
    updateLocalStorage({ ...formik.values, [field]: value });
  };
  //to refresh page after create event
  const handleReload = () => {
    window.location.reload();
  };

  const handleEventChange = (field, value) => {
    const selectedEvent = eventData.find((e) => e.name === value) || {
      id: "",
      name: value,
    };

    // Update Formik
    formik.setFieldValue(field, {
      event: selectedEvent.name,
      id: selectedEvent.id,
    });

    // Update local storage
    updateLocalStorage({
      ...formik.values,
      [field]: {
        event: selectedEvent.name,
        id: selectedEvent.id,
      },
    });
  };

  const calendarRef = useRef(null);

  const closeCalender = () => {
    calendarRef.current.hide();
  };

  const renderAutocomplete = (label, options, field) => (
    <Autocomplete
      options={filteredOptions}
      getOptionLabel={(option) => option}
      freeSolo
      disabled={preSelectedEvent !== null}
      value={formik.values[field].event}
      onFocus={() => {}}
      onInputChange={handleEventInputChange}
      onChange={(event, value) => {
        handleEventChange(field, value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          error={
            formik.touched.event?.event && Boolean(formik.errors.event?.event)
          }
          InputProps={{
            ...params.InputProps,
            sx: {
              display: "flex",
              padding: "10px 14px",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              alignSelf: "stretch",
              borderRadius: "8px",
              background: "#FFF",
              fontFamily: "Inter !important",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
            },
          }}
          InputLabelProps={{
            sx: {
              fontFamily: "Inter !important",
              color: "#888",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
            },
          }}
          onBlur={() => formik.setFieldTouched(field, true)} // Set touched for the field
        />
      )}
      renderOption={(props, option, { selected }) => (
        <li
          {...props}
          key={option + Math.random()}
          style={{ listStyle: "none" }}
        >
          {option}
        </li>
      )}
    />
  );

  return (
    <div
      className="event-form th-plan"
      style={{
        boxShadow: "none",
      }}
    >
      <span className="th-plan__text text-xl">What Event are you Planning</span>
      <form
        onSubmit={formik.handleSubmit}
        style={{
          width: "100%",
        }}
      >
        {renderAutocomplete(
          "Select Event - Type min 3 character",
          eventData.map((e) => e.name),
          "event"
        )}

        <TextField
          name="eventDesc"
          label="Whose event is it?"
          value={formik.values.eventDesc}
          onChange={handleFieldChange("eventDesc")} // Use common change handler
          size="small"
          className="w-full my-3"
          variant="outlined"
          InputProps={{
            // ...params.InputProps,
            sx: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              alignSelf: "stretch",
              borderRadius: "8px",
              background: "#FFF",
              fontFamily: "Inter !important",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
            },
          }}
          InputLabelProps={{
            sx: {
              fontFamily: "Inter !important",
              color: "#888",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
            },
          }}
        />

        <div style={{ marginBottom: "10px" }}>
          <Calendar
            baseZIndex={1100}
            panelClassName="th-custom_calendar_theming"
            view="date"
            minDate={today}
            maxDate={oneYearFromToday}
            showIcon
            icon={<EVENT_CALENDAR />}
            showTime
            dateFormat="dd-M-yy"
            value={formik.values.Date}
            onChange={(e) => {
              formik.setFieldValue("Date", e.value);
              updateLocalStorage({
                ...formik.values,
                Date: e.value && e.value.toString(),
              });
            }}
            placeholder="Event Date"
            ref={calendarRef}
            panelStyle={{ zIndex: 1100 }}
            footerTemplate={(e) => (
              <div className="p-datepicker-buttonbar">
                <button
                  aria-label="OK"
                  className="p-button-secondary p-button-text p-button p-component"
                  type="button"
                  style={{
                    background: "transparent",
                    border: "none",
                  }}
                  onClick={closeCalender}
                >
                  <span
                    className="p-button-label p-c"
                    style={{
                      textTransform: "initial",
                    }}
                  >
                    Select date
                  </span>
                </button>
              </div>
            )}
            inputStyle={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              alignSelf: "stretch",
              borderRadius: "8px",
              background: "#FFF",
              fontFamily: "Inter",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
              width: "100%",
              border:
                formik.touched.Date && formik.errors.Date
                  ? "1px solid red"
                  : "1px solid rgba(0, 0, 0, 0.23)",
            }}
            className="th-custom__calendar"
          />

          {formik.touched.Date && formik.errors.Date && (
            <p className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeSmall MuiFormHelperText-contained">
              {formik.errors.Date}
            </p>
          )}
        </div>

        {error && <p style={{ color: "red" }}>{error}</p>}

        <Button
          variant="contained"
          type="submit"
          className="mt-4 th-nav__provider__txt"
          sx={{
            display: "flex",
            textTransform: "initial",
            height: "48px",
            padding: "12px 24px",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            alignSelf: "stretch",
            borderRadius: "8px",
            background: "#383838",
            width: "100%",
          }}
        >
          Create my event
        </Button>
      </form>
      <ConfirmDialog />
    </div>
  );
};

export default ThurentEventForm;
